/**
 * Lazy import helper with one-time hard refresh retry for stale chunks.
 * This mitigates ChunkLoadError after a new deployment when clients still
 * reference old hashed chunk filenames.
 */

const RETRY_STORAGE_PREFIX = 'lazy-chunk-retry';
const RETRY_WINDOW_MS = 60 * 1000;

const isChunkLoadError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  const name = String(error?.name || '').toLowerCase();

  return (
    name.includes('chunkloaderror')
    || message.includes('loading chunk')
    || message.includes('chunkloaderror')
    || message.includes('failed to fetch dynamically imported module')
    || message.includes('importing a module script failed')
    || message.includes('module script')
  );
};

const getRetryKey = (moduleName) => `${RETRY_STORAGE_PREFIX}:${moduleName || 'unknown'}`;

const shouldReloadForChunkError = (moduleName) => {
  try {
    const key = getRetryKey(moduleName);
    const raw = sessionStorage.getItem(key);
    const lastTs = raw ? Number(raw) : 0;
    const now = Date.now();

    if (!lastTs || Number.isNaN(lastTs) || (now - lastTs) > RETRY_WINDOW_MS) {
      sessionStorage.setItem(key, String(now));
      return true;
    }
  } catch (err) {
    // If storage is unavailable, still attempt one reload as best effort.
    return true;
  }

  return false;
};

const clearRetryFlag = (moduleName) => {
  try {
    sessionStorage.removeItem(getRetryKey(moduleName));
  } catch (err) {
    // Ignore storage errors.
  }
};

const hardReload = () => {
  if (typeof window === 'undefined') return;

  const basePathRaw = process.env.PUBLIC_URL || '/';
  const basePath = basePathRaw.endsWith('/') ? basePathRaw : `${basePathRaw}/`;
  const url = new URL(basePath, window.location.origin);
  url.searchParams.set('__chunk_retry', String(Date.now()));
  window.location.replace(url.toString());
};

const lazyWithRetry = (importer, moduleName) => () =>
  importer()
    .then((module) => {
      clearRetryFlag(moduleName);
      return module;
    })
    .catch((error) => {
      if (typeof window !== 'undefined' && isChunkLoadError(error) && shouldReloadForChunkError(moduleName)) {
        hardReload();
        return new Promise(() => {});
      }
      throw error;
    });

export default lazyWithRetry;
export { isChunkLoadError };
